// @ts-check
/**
 * The page's single sizing model.
 *
 * Everything on this site — CSS geometry, typography and the WebGL camera —
 * is authored against one reference frame, 1440×900. Rather than hand-tuning
 * per-breakpoint numbers (which always leaves some other width overflowing),
 * each layer asks this module for the same two ratios and multiplies its own
 * authored values by them.
 *
 *   scale — spatial ratio, strictly proportional to the viewport
 *   type  — typographic ratio, a shallower line so small copy stays readable
 *
 * Consumers:
 *   src/main.js            publishes both as --scale / --type on <html>
 *   src/scenes/spirit-stage.js  dollies the camera by `scale` so the sculpture
 *                               occupies the same fraction of the frame it
 *                               does in the reference composition
 *
 * The WebGL half matters as much as the CSS half: a perspective camera with a
 * fixed FOV sizes its subject against the viewport *height* only, so on a
 * portrait phone the sculpture stayed pixel-identical while the layout around
 * it shrank — positions moved, the model's size did not.
 */

/** The viewport the composition was drawn at. */
export const DESIGN_WIDTH = 1440;
export const DESIGN_HEIGHT = 900;

/** Safety stop for `scale`. Deliberately far below any real device so the
 *  ratio stays proportional: at 390px wide it is 0.271. */
export const MIN_SCALE = 0.2;

/** Floor for `type` — what keeps an 0.65rem label readable on a phone.
 *  Lower it and the copy keeps shrinking but starts to disappear. */
export const MIN_TYPE = 0.8;

/** Narrow end of the type curve. */
export const MOBILE_WIDTH = 390;

/**
 * How the two axis ratios are blended into one size.
 *
 * Taking `min(widthRatio, heightRatio)` — a pure "contain" fit — honours only
 * the tighter axis, so on a portrait phone every element is sized for the
 * 390px width and the whole page reads as a miniature: the sculpture dropped
 * to 27% and the hall photographs to ~70px thumbnails.
 *
 * Weighting both axes keeps the composition honest while letting it grow:
 * the width still has the louder vote (it is what overflows), the height pulls
 * the size up so a tall screen is actually used. The stylesheet's
 * `min(<vw cap>, <px> × var(--scale))` rules remain the hard stop against
 * overflow, so a deliberately generous blend is safe — only the empty desktop
 * margins get cropped.
 */
export const WIDTH_WEIGHT = 0.7;
export const HEIGHT_WEIGHT = 0.3;

/**
 * @param {number} vw viewport width in CSS px
 * @param {number} vh viewport height in CSS px
 * @returns {number} spatial ratio, clamped to [MIN_SCALE, 1]
 */
export function computeScale(vw, vh) {
  const widthRatio = Math.min(1, vw / DESIGN_WIDTH);
  const heightRatio = Math.min(1, vh / DESIGN_HEIGHT);
  const blended = WIDTH_WEIGHT * widthRatio + HEIGHT_WEIGHT * heightRatio;
  return Math.min(1, Math.max(MIN_SCALE, blended));
}

/**
 * @param {number} vw viewport width in CSS px
 * @returns {number} typographic ratio, clamped to [MIN_TYPE, 1]
 */
export function computeType(vw) {
  // Interpolates between (390px → 0.80) and (1440px → 1.00). Continuous, so
  // every resize moves the copy — nothing looks stuck — while 0.80 stops an
  // 0.65rem label from collapsing at phone widths.
  const t = (vw - MOBILE_WIDTH) / (DESIGN_WIDTH - MOBILE_WIDTH);
  return Math.min(1, Math.max(MIN_TYPE, MIN_TYPE + (1 - MIN_TYPE) * t));
}

/** Reference aspect of the design frame. */
export const DESIGN_ASPECT = DESIGN_WIDTH / DESIGN_HEIGHT;

// ── Portrait weighting ──────────────────────────────────────────────────────
//
// `scale` above fixes how big things are; it says nothing about where they go.
// On a portrait phone that is the visible defect: the design frame is 1.74×
// the viewport width but only 0.50× its height, so the composition is cropped
// on both sides AND leaves ~40% of the screen empty at the bottom, with the
// subject pinned near the top.
//
// Two extra weights fix that, both driven by how far the viewport's shape is
// from the reference 1.6 aspect:
//
//   model gain    — how much the sculpture may exceed its reference size, so a
//                   tall screen is filled instead of framing a miniature
//   vertical split— how much of the spare height goes ABOVE the frame, which
//                   drops the sculpture into the empty lower half
//
// Both are exactly neutral at the reference, so the desktop composition is
// bit-for-bit unchanged.

/**
 * 0 at the reference 1.6 aspect, 1 at 0.8 (or anything taller).
 *
 * @param {number} vw
 * @param {number} vh
 * @returns {number}
 */
export function computeTallness(vw, vh) {
  const aspect = vw / vh;
  const t = (DESIGN_ASPECT - aspect) / (DESIGN_ASPECT / 2);
  return Math.min(1, Math.max(0, t));
}

/** The hero sculpture's share of the design frame, measured at 1440×900:
 *  520×725 px inside the 1440×900 frame. These are the *reference* targets a
 *  landscape viewport reproduces. */
export const HERO_FRAME_W = 520 / DESIGN_WIDTH;
export const HERO_FRAME_H = 725 / DESIGN_HEIGHT;

/** The same two shares on a tall portrait screen.
 *
 *  Width is the binding constraint there — a 1.4 h/w sculpture may not eat the
 *  whole width — so 0.90 is as close to the edges as the composition can go
 *  without bleeding, and the height target is looser so width decides. Going
 *  much below this is what leaves a portrait hero looking like a miniature
 *  floating in dead space: at 0.62 (the reference share) the sculpture is 250px
 *  wide on a 390px phone and the bottom 40% of the screen is empty. */
export const HERO_VIEW_W_TALL = 0.90;
export const HERO_VIEW_H_TALL = 0.62;

/** Multiplier for the WebGL camera's px-per-world-unit. 1 on a
 *  reference-shaped viewport; on a portrait one the sculpture grows until it
 *  either fills `HERO_VIEW_W_TALL` of the width or `HERO_VIEW_H_TALL` of the
 *  height.
 *
 *  @param {number} vw
 *  @param {number} vh
 *  @returns {number} ≥ 1
 */
export function computeModelGain(vw, vh) {
  const tall = computeTallness(vw, vh);
  const scale = computeScale(vw, vh);
  const byWidth = (HERO_FRAME_W + (HERO_VIEW_W_TALL - HERO_FRAME_W) * tall)
    * vw / (HERO_FRAME_W * DESIGN_WIDTH * scale);
  const byHeight = (HERO_FRAME_H + (HERO_VIEW_H_TALL - HERO_FRAME_H) * tall)
    * vh / (HERO_FRAME_H * DESIGN_HEIGHT * scale);
  return Math.max(1, Math.min(byWidth, byHeight));
}

/** Share of the spare vertical world placed above the design frame. 0.192 is
 *  the reference value (it puts the sculpture's centre at 30.8% of the frame);
 *  portrait viewports push the composition down so the lower half is used. */
export const SPLIT_ABOVE_REF = 0.192;
export const SPLIT_ABOVE_TALL = 0.30;
